import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { base_url } from '../../utils/config';
import qs from 'qs';


// ---------------- THUNKS ----------------

// Shops listing
export const fetchShops = createAsyncThunk(
  'home/fetchShops',
  async (_, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await axios.get(`${base_url}/api/sellers/shops`);
      return fulfillWithValue(data.shops);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchMoreProductsBySeller = createAsyncThunk(
  'home/fetchMoreProductsBySeller',
  async (productId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${base_url}/api/home/more-products/${productId}`
      );
      // data.moreProducts is the array
      return fulfillWithValue(data.moreProducts);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message
      );
    }
  }
);

// Categories
export const get_category = createAsyncThunk(
  'product/get_category',
  async (_, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${base_url}/api/home/get-categorys`);
      return fulfillWithValue(data);
    } catch (error) {
      console.log(error.response);
      return rejectWithValue(error);
    }
  }
);

// Fetch ALL products (unfiltered)
export const get_products = createAsyncThunk(
  'product/get_products',
  async (_, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${base_url}/api/home/get-products`);
      return fulfillWithValue(data);
    } catch (error) {
      console.log(error.response);
      return rejectWithValue(error);
    }
  }
);

// Fetch single product by slug (using unique id)
// Expecting the URL to be like "sareee-64d2f9abc123" so we extract the id.
export const get_product = createAsyncThunk(
  'product/get_product',
  async (productId, { fulfillWithValue, rejectWithValue }) => {
    try {
      console.log("Fetching product with id:", productId);
      const { data } = await axios.get(`${base_url}/api/home/product-get/${productId}`);
      console.log("API response:", data);
      return fulfillWithValue(data);
    } catch (error) {
      console.log("Error in get_product:", error.response);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

// Fetch the min/max price range
export const price_range_product = createAsyncThunk(
  'product/price_range_product',
  async (_, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${base_url}/api/home/price-range-latest-product`);
      return fulfillWithValue(data);
    } catch (error) {
      console.log(error.response);
      return rejectWithValue(error);
    }
  }
);

// Fetch Banners
export const get_banners = createAsyncThunk(
  'product/get_banners',
  async (_, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${base_url}/api/banners`);
      return fulfillWithValue(data);
    } catch (error) {
      console.log(error.response);
      return rejectWithValue(error);
    }
  }
);

export const add_banner_click_activity = createAsyncThunk(
  'userActivity/add_banner_click_activity',
  async ({ userId, userName, bannerCategory }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await axios.post(
        `${base_url}/api/banner-click`,
        { customerId: userId, customerName: userName, bannerCategory }
      );
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Query products by filters (category, price range, rating, etc.)
export const query_products = createAsyncThunk(
  'product/query_products',
  async (query, { fulfillWithValue, rejectWithValue }) => {
    try {
      // turn comma‐string into real array
      const categories = query.category
        ? query.category.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      const sellers = query.sellerId
        ? query.sellerId.split(',').map(s => s.trim()).filter(Boolean)
        : [];

      const params = {
        category: categories,       // array → repeated keys
        seller:   sellers,          // use `seller` here
        rating:   query.rating,
        lowPrice: query.low,
        highPrice: query.high,
        sortPrice: query.sortPrice,
        pageNumber: query.pageNumber,
        searchValue: query.searchValue || ''
      };

      const { data } = await axios.get(
        `${base_url}/api/home/query-products`,
        {
          params,
          paramsSerializer: params =>
            qs.stringify(params, { arrayFormat: 'repeat' })
        }
      );
      return fulfillWithValue(data);
    } catch (error) {
      console.log(error.response);
      return rejectWithValue(error);
    }
  }
);

// Query products specifically by sellerId
export const getProductsBySeller = createAsyncThunk(
  'product/query_products_by_seller',
  async (query, { fulfillWithValue, rejectWithValue }) => {
    try {
      const categories = query.category
        ? query.category.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      const sellers = query.sellerId
        ? query.sellerId.split(',').map(s => s.trim()).filter(Boolean)
        : [];

      const params = {
        category: categories,
        sellerId: sellers,        // back-end checks both seller and sellerId
        rating:   query.rating,
        lowPrice: query.low,
        highPrice: query.high,
        sortPrice: query.sortPrice,
        pageNumber: query.pageNumber,
        searchValue: query.searchValue || ''
      };

      const { data } = await axios.get(
        `${base_url}/api/home/query-products-by-seller`,
        {
          params,
          paramsSerializer: params =>
            qs.stringify(params, { arrayFormat: 'repeat' })
        }
      );
      return fulfillWithValue(data);
    } catch (error) {
      console.log(error.response);
      return rejectWithValue(error);
    }
  }
);

// Customer review
export const customer_review = createAsyncThunk(
  'review/customer_review',
  async (info, { fulfillWithValue, rejectWithValue, getState }) => {
    const { token } = getState().auth;
    const config = {
      headers: { Authorization: `Bearer ${token}` }
    };
    try {
      const { data } = await axios.post(`${base_url}/api/add-review`, info, config);
      return fulfillWithValue(data);
    } catch (error) {
      console.log(error.response);
      return rejectWithValue(error);
    }
  }
);

// Get reviews for a product
export const get_reviews = createAsyncThunk(
  'review/get_reviews',
  async ({ productId, pageNumber }, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${base_url}/api/home/customer/get-reviews/${productId}?pageNo=${pageNumber}`);
      console.log(data);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Track user activity
export const add_viewed_product = createAsyncThunk(
  'userActivity/add_viewed_product',
  async ({ userId, productId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await axios.post(
        `${base_url}/api/user-activity/${userId}/viewed`,
        { productId }
      );
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const add_cart_activity = createAsyncThunk(
  'userActivity/add_cart_activity',
  async ({ userId, productId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await axios.post(
        `${base_url}/api/user-activity/${userId}/cart`,
        { productId }
      );
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const add_buy_activity = createAsyncThunk(
  'userActivity/add_buy_activity',
  async ({ userId, productId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await axios.post(
        `${base_url}/api/user-activity/${userId}/buy`,
        { productId }
      );
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const get_user_activity = createAsyncThunk(
  'userActivity/get_user_activity',
  async ({ userId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await axios.get(`${base_url}/api/user-activity/${userId}`);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const add_swap_activity = createAsyncThunk(
  'userActivity/add_swap_activity',
  async ({ userId, productId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await axios.post(
        `${base_url}/api/user-activity/${userId}/swap`,
        { productId }
      );
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const add_wishlist_activity = createAsyncThunk(
  'userActivity/add_wishlist_activity',
  async ({ userId, productId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await axios.post(
        `${base_url}/api/user-activity/${userId}/wishlist`,
        { productId }
      );
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
// -------------- SLICE ----------------

export const homeReducer = createSlice({
  name: 'home',
  initialState: {
    categorys: [],
    products: [],
    totalProduct: 0,
    parPage: 4,
    latest_product: [],
    topRated_product: [],
    discount_product: [],
    priceRange: {
      low: 0,
      high: 100
    },
    product: {},
    relatedProducts: [],
    moreProducts: [],
    successMessage: '',
    errorMessage: '',
    totalReview: 0,
    rating_review: [],
    reviews: [],
    banners: [],
    shops: []
  },
  reducers: {
    messageClear: (state) => {
      state.successMessage = "";
      state.errorMessage = "";
    }
  },
  extraReducers: (builder) => {
    builder
      // ---------- GET PRODUCTS BY SELLER ----------
      .addCase(getProductsBySeller.fulfilled, (state, { payload }) => {
        state.products = payload.products;
        state.totalProduct =
          payload.totalProduct !== undefined
            ? payload.totalProduct
            : payload.products?.length ?? 0;
        state.parPage = payload.parPage || 4;
      })
      // ---------- FETCH SHOPS ----------
      .addCase(fetchShops.fulfilled, (state, action) => {
        state.shops = action.payload;
      })
      // ---------- GET CATEGORY ----------
      .addCase(get_category.fulfilled, (state, { payload }) => {
        state.categorys = payload.categorys;
      })
      // ---------- GET ALL PRODUCTS ----------
      .addCase(get_products.fulfilled, (state, { payload }) => {
        state.products = payload.products;
        state.totalProduct = payload.totalProduct || 0;
        state.parPage = payload.parPage || 4;
        state.latest_product = payload.latest_product || [];
        state.topRated_product = payload.topRated_product || [];
        state.discount_product = payload.discount_product || [];
      })
      // ---------- GET SINGLE PRODUCT + related etc. ----------
      .addCase(get_product.fulfilled, (state, { payload }) => {
        if (payload.product) {
          state.product = payload.product;
          state.relatedProducts = payload.relatedProducts || [];
          state.moreProducts = payload.moreProducts || [];
        } else {
          state.product = payload;
          state.relatedProducts = payload.relatedProducts || [];
          state.moreProducts = payload.moreProducts || [];
        }
      })
      // ---------- PRICE RANGE ----------
      .addCase(price_range_product.fulfilled, (state, { payload }) => {
        state.priceRange = payload.priceRange;
        state.latest_product = payload.latest_product;
      })
      // ---------- GET BANNERS ----------
      .addCase(get_banners.fulfilled, (state, { payload }) => {
        state.banners = payload.banners;
      })
      // ---------- QUERY PRODUCTS (filtering) ----------
      .addCase(query_products.fulfilled, (state, { payload }) => {
        state.products = payload.products;
        state.totalProduct =
          payload.totalProduct !== undefined
            ? payload.totalProduct
            : payload.products?.length ?? 0;
        state.parPage = payload.parPage || 4;
      })
      // ---------- CUSTOMER REVIEW ----------
      .addCase(customer_review.fulfilled, (state, { payload }) => {
        state.successMessage = payload.message;
      })
      // ---------- GET REVIEWS FOR A PRODUCT ----------
      .addCase(get_reviews.fulfilled, (state, { payload }) => {
        state.reviews = payload.reviews;
        state.totalReview = payload.totalReview;
        state.rating_review = payload.rating_review;
      })
      .addCase(fetchMoreProductsBySeller.fulfilled, (state, { payload }) => {
        state.moreProducts = payload;
      });

  }
});

export const { messageClear } = homeReducer.actions;
export default homeReducer.reducer;
